import { useState, useEffect, useCallback } from "react";
import { API } from "../lib/api";

/* ───────────────────── ROSTER PANEL ─────────────────────
   One roster editor, used by both the admin screen and the coach view.

   It exists as a shared component rather than a second copy because the two
   screens are the same job with a different key: the admin holds the club
   passcode, a coach holds a team session token that the API has already bound
   to their own team. Everything else — grouping, the email box, the staff
   toggle — has to behave identically or the two drift.

   `allowRemove` is the one real difference. Coaches don't get it: removing a
   player deletes their availability history with them, and the way back is a
   conversation with the club admin. Coaches build the roster up; admins are
   the ones who tear it down. */
export function RosterPanel({ teamId, auth, allowRemove = false, onChanged }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Admin sends the club passcode; a coach sends their team token. The API
  // accepts either for roster writes and scopes the token to its own team.
  const headers = useCallback(
    (json) => ({
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...(auth.adminCode ? { "x-admin-code": auth.adminCode } : { "x-auth-token": auth.token }),
    }),
    [auth.adminCode, auth.token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API + "/roster/" + teamId, { headers: headers(false) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setPlayers(data.players || []);
      if (onChanged) onChanged(data.players || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
    // onChanged is deliberately NOT a dependency: callers pass an inline
    // function, so depending on it would reload the roster on every render of
    // the parent — and each reload calls onChanged again.
  }, [teamId, headers]);       // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  async function write(method, body, describe) {
    setError(""); setSuccess("");
    try {
      const res = await fetch(API + "/roster/" + teamId, {
        method,
        headers: headers(true),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText);
      setSuccess(describe(data));
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  // "Alex Johnson, 7, mom@example.com" — jersey and email are both optional and
  // recognized in either order after the name, because that's how people type
  // them. Commas only count as separators once a field is recognizably a jersey
  // or an email: otherwise the whole line is the name, so someone entering
  // "Johnson, Alex" still gets a player called that and not jersey "Alex".
  const parseLine = (line) => {
    const parts = line.split(",").map((s) => s.trim()).filter(Boolean);
    const extras = parts.slice(1);
    const parentEmail = (extras.find((s) => s.includes("@")) || "").toLowerCase();
    const jerseyNumber = extras.find((s) => /^\d{1,3}$/.test(s)) || "";
    if (!parentEmail && !jerseyNumber) {
      return { name: line.trim(), jerseyNumber: "", parentEmail: "" };
    }
    return { name: parts[0] || "", jerseyNumber, parentEmail };
  };

  const addPeople = (role) => {
    const people = input
      .split("\n").map((l) => l.trim()).filter(Boolean)
      .map(parseLine).filter((p) => p.name)
      .map((p) => ({ ...p, role }));
    if (people.length === 0) return;
    const noun = role === "staff" ? "coach/staff" : "player";
    setInput("");
    return write("POST", { players: people }, () =>
      people.length + " " + noun + (people.length !== 1 ? "s" : "") + " added");
  };

  // The email can go on at any point in the season, not just at roster upload —
  // families get their link the moment it's set, and a blank clears it.
  const setEmail = (player) => {
    const current = player.parentEmail || "";
    const entered = window.prompt(
      "Email for " + player.name + "\n\n" +
      "Their personal link is sent here. Use the same email on every one of a " +
      "family's players — including players on other teams — and they get one " +
      "link covering all of them. Leave it blank to remove it.",
      current
    );
    if (entered === null) return;              // cancelled — NOT the same as cleared
    const next = entered.trim().toLowerCase();
    if (next === current) return;
    return write("PUT", { oldName: player.name, newName: player.name, parentEmail: next }, () =>
      next ? "Email for " + player.name + " set to " + next
           : "Removed the email for " + player.name);
  };

  const rename = (player) => {
    const newName = window.prompt("Edit name:", player.name);
    if (!newName || newName.trim() === player.name) return;
    return write("PUT", { oldName: player.name, newName: newName.trim() }, (data) =>
      "Renamed \"" + player.name + "\" to \"" + newName.trim() + "\"" +
      (data.availabilityUpdated > 0 ? " (" + data.availabilityUpdated + " availability records updated)" : ""));
  };

  const setRole = (player, role) =>
    write("PUT", { oldName: player.name, newName: player.name, role }, () =>
      player.name + (role === "staff" ? " is now coach/staff" : " is now a player"));

  const remove = async (player) => {
    if (!window.confirm("Remove " + player.name + " from the roster?\n\nThis also deletes their availability.")) return;
    setError(""); setSuccess("");
    try {
      const res = await fetch(API + "/roster/" + teamId + "?player=" + encodeURIComponent(player.name), {
        method: "DELETE",
        headers: headers(false),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setSuccess(player.name + " removed from the roster");
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const withEmail = players.filter((p) => p.parentEmail).length;

  if (loading) return <p className="text-xs text-gray-400 animate-pulse">Loading roster…</p>;

  return (
    <>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {success && <p className="text-xs text-green-700 mb-2">{success}</p>}

      {players.length > 0 && (
        <>
          <p className="text-xs text-gray-500 mb-2">
            {withEmail} of {players.length} have an email on file
            {withEmail < players.length ? " — click ✉ to add one, the rest keep using the team passcode" : ""}
          </p>
          {[
            { role: "player", label: "Players" },
            { role: "staff", label: "Coaches & staff" },
          ].map(({ role, label }) => {
            const group = players.filter((p) => (p.role || "player") === role);
            if (group.length === 0) return null;
            const staff = role === "staff";
            return (
              <div key={role} className="mb-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  {label} ({group.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {group.map((p) => (
                    <span key={p.name}
                      className={"inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border " +
                        (staff ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-purple-50 text-purple-700 border-purple-200")}>
                      {p.jerseyNumber && <span className="font-bold">#{p.jerseyNumber}</span>}
                      {p.name}
                      <button onClick={() => setEmail(p)}
                        className={"ml-0.5 " + (p.parentEmail
                          ? "text-emerald-600 hover:text-emerald-700"
                          : (staff ? "text-sky-300" : "text-purple-300") + " hover:text-blue-600")}
                        title={p.parentEmail ? p.parentEmail + " — click to change or remove" : "No email — click to add one"}>✉</button>
                      <button onClick={() => rename(p)}
                        className={(staff ? "text-sky-400" : "text-purple-400") + " hover:text-blue-600"}
                        title="Edit name">✎</button>
                      <button onClick={() => setRole(p, staff ? "player" : "staff")}
                        className={(staff ? "text-sky-400" : "text-purple-400") + " hover:text-blue-600"}
                        title={staff ? "Move to players" : "Move to coaches & staff"}>⇄</button>
                      {allowRemove && (
                        <button onClick={() => remove(p)}
                          className={(staff ? "text-sky-400" : "text-purple-400") + " hover:text-red-500 font-bold"}
                          title="Remove">×</button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={"One per line — jersey and email optional:\nAlex Johnson\nSam Williams, 12\nJordan Smith, 9, jordan.smith.parent@example.com"}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-400 min-h-[80px] mb-2"
      />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => addPeople("player")}
          disabled={!input.trim()}
          className="px-4 py-1.5 text-xs text-white rounded-lg transition-colors shadow-sm disabled:opacity-50"
          style={{ backgroundColor: "#7c3aed" }}>
          Add Players
        </button>
        <button onClick={() => addPeople("staff")}
          disabled={!input.trim()}
          className="px-4 py-1.5 text-xs text-white rounded-lg transition-colors shadow-sm disabled:opacity-50"
          style={{ backgroundColor: "#0284c7" }}>
          Add as Coach/Staff
        </button>
      </div>
      {!allowRemove && players.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-2">
          Need someone taken off the roster? The club admin can remove them — that also
          deletes their availability, so it isn't undoable.
        </p>
      )}
    </>
  );
}
